/**
 * mockupHtml.js — builds the self-contained, shareable mockup web page.
 *
 * Mirrors the Reports publish pattern: we generate one HTML string, upload it
 * to the `published-mockups` bucket, and serve it at /m/<id> (see src/index.js).
 * The page is an Instagram-feed grid (like July RF mockup.pdf) dressed in the
 * SAME STR palette + fonts as the published Reports (brass #A98953,
 * obsidian #0A0A0C, bone #EFEBE4, Bodoni Moda + Inter Tight + JetBrains Mono). Interactive lightbox:
 * click a post to open it big; reels play (direct video) or link out; carousels slide.
 */

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Render a caption with the handle bold, like Instagram.
function captionHtml(handle, caption) {
  return `<span class="handle">${esc(handle || '')}</span> ${esc(caption || '')}`;
}

export function buildMockupHtml({ title, monthLabel, handle = '', logoUrl = '', cells = [] }) {
  // STR palette — obsidian ground, bone type, one brass hairline (matches ReportsPage published view).
  const GOLD = '#A98953', INK = '#0A0A0C', SURF = '#141417', SURF2 = '#17171A',
    LINE = 'rgba(239,235,228,0.12)', TXT = '#EFEBE4', CAP = 'rgba(239,235,228,0.72)', MUT = 'rgba(239,235,228,0.55)', FAINT = 'rgba(239,235,228,0.42)';

  const cardsHtml = cells.map((cell, i) => {
    const type = cell.type || 'Post';
    const badge = type === 'Reel'
      ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#fff" stroke-width="2"><polygon points="10 8 16 12 10 16 10 8" fill="#fff" stroke="none"/><rect x="3" y="4" width="18" height="16" rx="4"/></svg>'
      : type === 'Carousel'
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fff" stroke-width="2"><rect x="7" y="3" width="14" height="14" rx="3"/><path d="M3 7v10a4 4 0 0 0 4 4h10"/></svg>'
        : '';
    const cover = cell.coverUrl
      ? `<img src="${esc(cell.coverUrl)}" alt="" loading="lazy"/>`
      : `<div class="ph">${esc(type)}</div>`;
    return `
      <div class="post" data-i="${i}">
        <div class="media">
          ${cover}
          ${badge ? `<div class="ticon">${badge}</div>` : ''}
          <div class="playhint">${type === 'Reel' ? '▶' : (type === 'Carousel' ? '⧉' : '⤢')}</div>
        </div>
        <div class="acts"><span>♡</span><span>💬</span><span>➤</span><span class="save">⛉</span></div>
        <div class="likes">${esc(cell.likes || '50 likes')}</div>
        <div class="cap">${captionHtml(handle, cell.caption)}</div>
        <div class="date">${esc(cell.date || '')}</div>
      </div>`;
  }).join('');

  const dataJson = JSON.stringify(cells.map(c => ({
    type: c.type || 'Post', coverUrl: c.coverUrl || '', reelUrl: c.reelUrl || '', driveUrl: c.driveUrl || '',
    carouselUrls: c.carouselUrls || [], caption: c.caption || '', date: c.date || '', likes: c.likes || '50 likes',
  }))).replace(/</g, '\\u003c');

  return `<!doctype html><html lang="en"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(title || 'Mockup')} · Strategists</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400&family=Bodoni+Moda:opsz,wght@6..96,400;6..96,500&family=Inter+Tight:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box}
  html,body{margin:0;background:${INK}}
  body{color:${TXT};font-family:'Inter Tight',system-ui,sans-serif;font-weight:300;-webkit-font-smoothing:antialiased}
  .wrap{max-width:1040px;margin:0 auto;padding:52px 20px 64px}
  .head{text-align:center;margin-bottom:40px;display:flex;flex-direction:column;align-items:center}
  .head img{max-height:76px;max-width:280px;object-fit:contain;margin-bottom:14px}
  .head .brand{font-family:'Bodoni Moda','Didot',Georgia,serif;font-weight:400;font-size:30px;letter-spacing:.06em;color:${TXT}}
  .head .hair{width:34px;height:1px;background:${GOLD};opacity:.6;margin:13px 0 11px}
  .head .ttl{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;font-weight:400;letter-spacing:.28em;text-transform:uppercase;color:${FAINT}}
  .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:26px 22px}
  @media(max-width:720px){.grid{grid-template-columns:1fr;max-width:420px;margin:0 auto}}
  .post{background:${SURF};border:1px solid ${LINE};border-radius:2px;overflow:hidden;cursor:pointer;transition:transform .18s,border-color .18s}
  .post:hover{transform:translateY(-3px);border-color:${GOLD}}
  .media{position:relative;width:100%;aspect-ratio:4/5;background:${SURF2};overflow:hidden}
  .media img{width:100%;height:100%;object-fit:cover;display:block}
  .media .ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:${FAINT};font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.18em;text-transform:uppercase}
  .ticon{position:absolute;top:10px;right:10px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5))}
  .playhint{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:34px;color:#fff;opacity:0;transition:opacity .18s;text-shadow:0 2px 10px rgba(0,0,0,.6)}
  .post:hover .playhint{opacity:.95}
  .acts{display:flex;gap:14px;padding:11px 13px 4px;font-size:16px;color:${TXT};opacity:.9}
  .acts .save{margin-left:auto}
  .likes{padding:2px 13px;font-family:'JetBrains Mono',monospace;font-weight:400;font-size:11.5px;letter-spacing:.02em;color:${TXT}}
  .cap{padding:5px 13px 0;font-size:13px;line-height:1.55;color:${CAP};white-space:pre-wrap}
  .cap .handle{font-weight:600;color:${GOLD}}
  .date{padding:9px 13px 14px;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.06em;color:${FAINT}}
  .foot{margin-top:56px;border-top:1px solid ${LINE};padding-top:30px;display:flex;flex-direction:column;align-items:center}
  .foot .fm{font-family:'Bodoni Moda','Didot',Georgia,serif;font-weight:400;font-size:24px;letter-spacing:.28em;text-indent:.28em;color:${TXT};line-height:1}
  .foot .fh{width:30px;height:1px;background:${GOLD};opacity:.6;margin:12px 0 9px}
  .foot .fd{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:8.5px;letter-spacing:.28em;text-transform:uppercase;color:${FAINT}}
  #pdfbtn{position:fixed;bottom:20px;right:20px;z-index:40;background:${GOLD};color:${INK};border:1px solid ${GOLD};border-radius:2px;padding:12px 20px;font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:400;letter-spacing:.14em;text-transform:uppercase;cursor:pointer;box-shadow:0 10px 30px rgba(0,0,0,.5)}
  @media print{
    html,body{margin:0;background:${INK};-webkit-print-color-adjust:exact;print-color-adjust:exact}
    #pdfbtn,.lb{display:none!important}
    .wrap{width:1040px;max-width:none;margin:0 auto;padding:18px}
    .grid{grid-template-columns:repeat(3,1fr) !important}
    .post{break-inside:avoid;page-break-inside:avoid}
    .post:hover{transform:none}
  }
  .lb{position:fixed;inset:0;background:rgba(10,10,12,.95);display:none;align-items:center;justify-content:center;z-index:50;padding:20px}
  .lb.open{display:flex}
  .lb .box{max-width:min(480px,92vw);width:100%}
  .lb .frame{position:relative;width:100%;aspect-ratio:4/5;background:#000;border:1px solid ${LINE};border-radius:2px;overflow:hidden;display:flex;align-items:center;justify-content:center}
  .lb .frame img,.lb .frame video{width:100%;height:100%;object-fit:contain;background:#000}
  .lb .cap{color:${CAP};font-size:13.5px;line-height:1.55;padding:14px 4px 0;max-height:26vh;overflow:auto}
  .lb .cap .handle{font-weight:600;color:${GOLD}}
  .lb .x{position:absolute;top:16px;right:20px;color:${TXT};font-size:30px;cursor:pointer;line-height:1;z-index:2}
  .lb .nav{position:absolute;top:50%;transform:translateY(-50%);color:${TXT};font-size:40px;cursor:pointer;user-select:none;padding:0 14px;opacity:.75}
  .lb .prev{left:0}.lb .next{right:0}
  .lb .open-ext{display:inline-block;margin-top:12px;color:${GOLD};font-family:'JetBrains Mono',monospace;font-weight:400;font-size:10px;letter-spacing:.12em;text-transform:uppercase;text-decoration:none;border:1px solid ${GOLD};padding:9px 16px;border-radius:2px}
  .lb .dots{display:flex;gap:6px;justify-content:center;margin-top:10px}
  .lb .dots i{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,.35)}
  .lb .dots i.on{background:#fff}
</style></head>
<body>
  <div class="wrap">
    <div class="head">
      ${logoUrl ? `<img src="${esc(logoUrl)}" alt=""/>` : `<div class="brand">${esc(handle || title || '')}</div>`}
      <div class="hair"></div>
      <div class="ttl">${esc(monthLabel || title || '')} Mockup</div>
    </div>
    <div class="grid">${cardsHtml}</div>
    <div class="foot"><div class="fm">STR</div><div class="fh"></div><div class="fd">Strategists</div></div>
  </div>

  <button id="pdfbtn" onclick="printAll()">Download PDF</button>

  <div class="lb" id="lb">
    <span class="x" onclick="LB.close()">×</span>
    <span class="nav prev" onclick="LB.step(-1)">‹</span>
    <span class="nav next" onclick="LB.step(1)">›</span>
    <div class="box">
      <div class="frame" id="lbframe"></div>
      <div class="dots" id="lbdots"></div>
      <div class="cap" id="lbcap"></div>
    </div>
  </div>

  <script>
  var CELLS = ${dataJson};
  var HANDLE = ${JSON.stringify(handle || '')};
  var LB = (function(){
    var idx = 0, slide = 0;
    var lb = document.getElementById('lb'), frame = document.getElementById('lbframe'),
        cap = document.getElementById('lbcap'), dots = document.getElementById('lbdots');
    function isVid(u){ return /\\.(mp4|webm|mov|m4v)(\\?|$)/i.test(u||''); }
    function render(){
      var c = CELLS[idx]; if(!c) return;
      var html='', imgs = (c.carouselUrls&&c.carouselUrls.length)?c.carouselUrls:(c.coverUrl?[c.coverUrl]:[]);
      dots.innerHTML='';
      if(c.type==='Reel'){
        if(isVid(c.reelUrl)){ html='<video src="'+c.reelUrl+'" controls autoplay playsinline></video>'; }
        else if(c.coverUrl){ html='<img src="'+c.coverUrl+'"/>'; }
        else { html='<div style="color:#fff">No preview</div>'; }
      } else {
        var src = imgs[slide]||imgs[0]||'';
        html = src?'<img src="'+src+'"/>':'<div style="color:#fff">No image</div>';
        if(imgs.length>1){ for(var k=0;k<imgs.length;k++){ dots.innerHTML+='<i class="'+(k===slide?'on':'')+'"></i>'; } }
      }
      frame.innerHTML = html;
      var capHtml = '<span class="handle">'+HANDLE+'</span> ' + (c.caption||'').replace(/</g,'&lt;');
      if(c.type==='Reel' && c.reelUrl && !isVid(c.reelUrl)){
        capHtml += '<br><a class="open-ext" href="'+c.reelUrl+'" target="_blank" rel="noreferrer">Open reel ↗</a>';
      }
      if(c.driveUrl){
        capHtml += '<br><a class="open-ext" href="'+c.driveUrl+'" target="_blank" rel="noreferrer">'+(c.type==='Carousel'?'Open carousel in Drive ↗':'Open in Drive ↗')+'</a>';
      }
      cap.innerHTML = capHtml;
    }
    return {
      open:function(i){ idx=i; slide=0; lb.classList.add('open'); render(); },
      close:function(){ lb.classList.remove('open'); frame.innerHTML=''; },
      step:function(d){
        var c=CELLS[idx];
        var imgs=(c&&c.carouselUrls&&c.carouselUrls.length)?c.carouselUrls:[];
        if(c && c.type==='Carousel' && imgs.length>1){ slide=(slide+d+imgs.length)%imgs.length; render(); return; }
        idx=(idx+d+CELLS.length)%CELLS.length; slide=0; render();
      }
    };
  })();
  document.querySelectorAll('.post').forEach(function(el){
    el.addEventListener('click',function(){ LB.open(parseInt(el.getAttribute('data-i'),10)); });
  });
  document.getElementById('lb').addEventListener('click',function(e){ if(e.target===this) LB.close(); });
  document.addEventListener('keydown',function(e){
    if(!document.getElementById('lb').classList.contains('open')) return;
    if(e.key==='Escape') LB.close(); if(e.key==='ArrowRight') LB.step(1); if(e.key==='ArrowLeft') LB.step(-1);
  });
  function printAll(){
    var wrap=document.querySelector('.wrap');
    // Measure at the SAME width + padding the print layout uses, so the page
    // height matches the content exactly — no uncovered (white) strip.
    var prevW=wrap.style.width, prevP=wrap.style.padding;
    wrap.style.width='1040px'; wrap.style.padding='18px';
    function go(){
      var h=Math.ceil(wrap.getBoundingClientRect().height);
      var st=document.getElementById('pageStyle')||document.createElement('style');
      st.id='pageStyle'; st.textContent='@page{size:1040px '+h+'px;margin:0}';
      document.head.appendChild(st);
      setTimeout(function(){ window.print(); wrap.style.width=prevW; wrap.style.padding=prevP; }, 60);
    }
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ setTimeout(go,40); }); } else { go(); }
  }
  if(location.search.indexOf('print')>=0){ window.addEventListener('load',function(){ setTimeout(printAll, 900); }); }
  </script>
</body></html>`;
}
